// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cycleDialogFocus } from "./focusCycle.js";
import { Button } from "../ui/Button.js";
import { IconX } from "@tabler/icons-react";

import {
  type PayrollAuditKind,
  openPayrollAuditOverlay,
} from "./PayrollAuditLinks";
import { PayrollBusinessAuditPage } from "../../routes/payrollBusinessAudit";
import { PayrollChangesAuditPage } from "../../routes/payrollChangesAudit";
import { PayrollTechnicalAuditPage } from "../../routes/payrollTechnicalAudit";
import {
  payrollAuditFilterSummary,
  payrollAuditFiltersFromSearch,
} from "../../lib/payrollAuditDisplay";
import { payrollAuditLevelConfig } from "./PayrollAuditPage";

const auditKinds: PayrollAuditKind[] = ["business", "changes", "technical"];

const standaloneAuditPaths = new Set([
  "/payroll/business-audit",
  "/payroll/changes-audit",
  "/admin/payroll/technical-audit",
]);

export const isPayrollAuditOverlayHostPath = (pathname: string) =>
  (pathname === "/" ||
    pathname.startsWith("/period-summary") ||
    pathname.startsWith("/work-profiles") ||
    pathname.startsWith("/payroll")) &&
  !standaloneAuditPaths.has(pathname);

export const payrollAuditOverlayPortalTarget = (
  trigger: HTMLElement | null,
): HTMLElement =>
  trigger?.closest<HTMLElement>(".period-detail-modal, .tapp-modal__dialog") ??
  document.body;

const currentKind = (): PayrollAuditKind | null => {
  const value = new URLSearchParams(window.location.search).get("auditOverlay");
  return auditKinds.includes(value as PayrollAuditKind)
    ? (value as PayrollAuditKind)
    : null;
};

export const closePayrollAuditOverlay = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete("auditOverlay");
  const returnUrl =
    window.history.state?.payrollAuditReturnUrl ??
    `${window.location.pathname}${params.size ? `?${params}` : ""}`;
  window.history.replaceState(
    { ...(window.history.state ?? {}), payrollAuditOverlay: false },
    "",
    returnUrl,
  );
  // Closing from X, backdrop, or Escape must not remount the payroll route:
  // doing so would discard an employee or exact-value panel underneath.
  window.dispatchEvent(new Event("payroll-audit-overlay-change"));
};

export function PayrollAuditOverlay({ pathname }: { pathname: string }) {
  const [kind, setKind] = useState<PayrollAuditKind | null>(() =>
    isPayrollAuditOverlayHostPath(pathname) ? currentKind() : null,
  );
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement>(
    () => document.body,
  );
  const isOpen = Boolean(kind);

  useEffect(() => {
    const sync = (event?: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      if (
        detail?.trigger &&
        !detail.trigger.closest?.(".payroll-audit-overlay")
      ) {
        triggerRef.current = detail.trigger;
        setPortalTarget(payrollAuditOverlayPortalTarget(detail.trigger));
      }
      setKind(isPayrollAuditOverlayHostPath(pathname) ? currentKind() : null);
    };
    window.addEventListener("popstate", sync);
    window.addEventListener("payroll-audit-overlay-change", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("payroll-audit-overlay-change", sync);
    };
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      dialogRef.current?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePayrollAuditOverlay();
        return;
      }
      cycleDialogFocus(event, dialogRef.current, true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      window.requestAnimationFrame(() => {
        if (triggerRef.current?.isConnected) triggerRef.current.focus();
      });
    };
  }, [isOpen]);

  if (!kind || !isPayrollAuditOverlayHostPath(pathname)) return null;

  const visibleAuditKinds = auditKinds;
  const content =
    kind === "business" ? (
      <PayrollBusinessAuditPage embedded />
    ) : kind === "changes" ? (
      <PayrollChangesAuditPage embedded />
    ) : (
      <PayrollTechnicalAuditPage embedded />
    );
  const context = new URLSearchParams(window.location.search);
  const filterSummary = payrollAuditFilterSummary(
    payrollAuditFiltersFromSearch(window.location.search, kind),
  );

  return createPortal(
    <div className="payroll-audit-overlay" role="presentation">
      <button
        type="button"
        className="payroll-audit-overlay__backdrop"
        aria-label="Close audit"
        onClick={closePayrollAuditOverlay}
      />
      <section
        ref={dialogRef}
        className="payroll-audit-overlay__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payroll-audit-overlay-title"
        tabIndex={-1}
      >
        <header className="payroll-audit-overlay__header">
          <div className="min-w-0">
            <h1
              id="payroll-audit-overlay-title"
              className="text-xl font-semibold text-foreground"
            >
              Payroll audit
            </h1>
            <p className="mt-1 text-sm text-foreground-muted">
              {filterSummary.length
                ? filterSummary.join(", ")
                : "All payroll activity"}
            </p>
          </div>
          <Button variant="ghost" iconOnly
            type="button"
            onClick={closePayrollAuditOverlay}
            aria-label="Close audit"
            className="payroll-audit-overlay__close"
          >
            <IconX size={20} aria-hidden="true" />
          </Button>
        </header>
        <nav
          className="payroll-audit-overlay__tabs"
          aria-label="Audit detail level"
        >
          {visibleAuditKinds.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={kind === item}
              onClick={(event) =>
                openPayrollAuditOverlay(
                  item,
                  {
                    month: context.get("month") ?? "",
                    periodThrough: context.get("periodThrough") ?? "",
                    employeeId: Number(context.get("employeeId")) || undefined,
                    userId: Number(context.get("userId")) || undefined,
                    fieldKey: context.get("fieldKey") as never,
                  },
                  event.currentTarget,
                  true,
                )
              }
            >
              {payrollAuditLevelConfig[item].label}
            </button>
          ))}
        </nav>
        <div className="payroll-audit-overlay__body">{content}</div>
      </section>
    </div>,
    portalTarget.isConnected ? portalTarget : document.body,
  );
}
